import React from 'react'
import { Box, Button, CircularProgress, IconButton, Modal, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'
import { digitalAppApi } from '../../../../store/services/digitalAppApi'
import usePdfBlobUrl from '../../../../hooks/usePdfBlobUrl'
import { buildAlbaranPrintParams } from '../../../../utils/buildFacturaPrintParams'
import { buildAlbaranFilename } from '../../../../utils/buildAlbaranFilename'
import IvaPriceSwitch from '../../../../components/IvaPriceSwitch/IvaPriceSwitch'

const AlbaranModal = ({ open, onClose, selectedOrder = null, tradeNameFallback = '' }) => {
  const orderId = selectedOrder?._id ?? (typeof selectedOrder === 'string' ? selectedOrder : null)
  const orderForFilename = selectedOrder?._id ? selectedOrder : { _id: orderId, tradeName: tradeNameFallback }
  const priceWithIva = useSelector((state) => state.contextUI.PriceIVA)

  const queryArgs = buildAlbaranPrintParams(
    orderId,
    selectedOrder?.tradeName || selectedOrder?.userAcc?.tradeName || tradeNameFallback,
    priceWithIva,
  )

  const {
    data: albaranData,
    isLoading: isLoadingAlbaran,
    isError: isErrorAlbaran,
    isSuccess,
  } = digitalAppApi.useGetAlbaranByIdQuery(queryArgs, {
    skip: !(open && orderId && queryArgs),
  })

  const pdfBlobUrl = usePdfBlobUrl(albaranData, isSuccess)

  const handleDownloadServerPdf = () => {
    if (!pdfBlobUrl) return
    const link = document.createElement('a')
    link.href = pdfBlobUrl
    link.download = buildAlbaranFilename(orderForFilename, tradeNameFallback)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleOpenServerPdf = () => {
    if (pdfBlobUrl) window.open(pdfBlobUrl, '_blank')
  }

  if (!orderId) return null

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="albaran-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: 520 },
          maxHeight: '92vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography id="albaran-modal-title" variant="h6" component="h2" gutterBottom>
            Albarán (servidor)
          </Typography>
          <IconButton onClick={onClose} className="driver-comments-modal__close">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pedido: {orderId?.slice(-6) || 'N/A'}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <IvaPriceSwitch compact />
        </Box>

        <Typography
          variant="body2"
          color={
            isLoadingAlbaran ? 'warning.main' : isErrorAlbaran ? 'error.main' : pdfBlobUrl ? 'success.main' : 'text.secondary'
          }
        >
          PDF:{' '}
          {isLoadingAlbaran ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress color="warning" size={14} /> Cargando…
            </Box>
          ) : isErrorAlbaran ? (
            'Error'
          ) : pdfBlobUrl ? (
            'Listo'
          ) : (
            'Esperando…'
          )}
        </Typography>

        {pdfBlobUrl && !isLoadingAlbaran && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" onClick={handleDownloadServerPdf}>
              Descargar
            </Button>
            <Button variant="outlined" onClick={handleOpenServerPdf}>
              Abrir
            </Button>
          </Box>
        )}

        {isErrorAlbaran && (
          <Typography color="error" sx={{ mt: 2 }}>
            No se pudo obtener el documento del servidor.
          </Typography>
        )}

        {pdfBlobUrl && !isLoadingAlbaran && (
          <Box sx={{ height: { xs: '50dvh', sm: '60dvh' }, width: '100%', mt: 2, border: '1px solid #ccc' }}>
            <iframe src={pdfBlobUrl} title="Albarán PDF" width="100%" height="100%" style={{ border: 'none' }} />
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default React.memo(AlbaranModal)
